import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LuListPlus, LuListRestart } from 'react-icons/lu';
import { toast } from 'react-toastify';
import ModalLayout from '../layouts/ModalLayout';
import IconBadge from '../icons/IconBadge';
import Labeled from '../misc/Labeled';
import Button from '../buttons/Button';
import ConnectionsList from '../misc/ConnectionsList';
import { ConnectionsListConnection } from '../../../types/misc';
import TabButtons from '../buttons/TabButtons';
import { ScoreCardFormData, ScoreCardType } from '../../../models/scoreCard.model';

interface ScoreCardModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit: (data: ScoreCardFormData) => Promise<void>;
  onClosed?: () => void;
  initialState?: ScoreCardFormData;
  membersOfCommunity: ConnectionsListConnection[];
}


export default function ScoreCardModal({
  open,
  setOpen,
  onSubmit,
  initialState,
  onClosed,
  membersOfCommunity,
}: ScoreCardModalProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.lists.score.forms' });
  const { t: errorT } = useTranslation('', { keyPrefix: 'page.lists.score.error' });
  const [scoreList, setScoreList] = React.useState<ScoreCardFormData>({
    name: '',
    memberEditAccess: true,
    type: ScoreCardType.COUNT,
  });
  const [selectedMembersIdsList, setSelectedMembersIdsList] = React.useState<number[]>([]);

  useEffect(() => {
    setScoreList((prev) => ({
      ...prev,
      selectedUserIds: selectedMembersIdsList,
    }));
  }, [selectedMembersIdsList]);

  useEffect(() => {
    if (initialState) {
      setScoreList({
        ...initialState,
        selectedUserIds: selectedMembersIdsList,
      });
    } else {
      setScoreList({
        name: '',
        memberEditAccess: true,
        containsAllMembers: true,
        type: ScoreCardType.COUNT,
      });
    }
    setSelectedMembersIdsList([]);
  }, [initialState]);

  useEffect(() => {
    if (!open && onClosed) {
      onClosed();
    }

    if (!open && !initialState) {
      setScoreList({
        name: '',
        memberEditAccess: true,
        containsAllMembers: true,
        type: ScoreCardType.COUNT,
      });
    }
    setSelectedMembersIdsList([]);
  }, [open, onClosed]);

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 w-full max-w-[600px] rounded-xl bg-secondary-50 p-6">
      <div className="flex items-center gap-4 border-b border-secondary-200 pb-2">
        <IconBadge icon={initialState ? LuListRestart : LuListPlus} />
        <h2 className="text-lg font-semibold">{t('createTitle')}</h2>
      </div>
      <div className="flex flex-col gap-4 pt-2">
        <Labeled label={t('name')}>
          <input
            value={scoreList.name}
            onChange={(ev) => setScoreList((prev) => ({ ...prev, name: ev.target.value }))}
            type="text"
            placeholder={t('namePlaceholder')}
          />
        </Labeled>
        <Labeled label={t('description')}>
          <textarea
            value={scoreList.description ?? ''}
            onChange={(ev) => setScoreList((prev) => ({ ...prev, description: ev.target.value }))}
            placeholder={t('descriptionPlaceholder')}
            rows={3}
          />
        </Labeled>
        <p className="font-medium">{t('ascending')}</p>
        <TabButtons
          initialState={scoreList.ascending}
          containerClassName="bg-secondary-200"
          highlightClassName="bg-secondary-50"
          buttonClassName="px-2"
          buttonSelectedClassName="px-2"
          options={[
            { label: t('highest'), value: false as const },
            { label: t('lowest'), value: true as const },
          ]}
          onTabChange={(value) => {
            setScoreList((prev) => ({
              ...prev,
              ascending: value,
            }));
          }}
        />
        {initialState ? (
          <>
            <p className="font-medium">{t('typeCurrent')}</p>
            <p className="text-base text-secondary-600">
              {scoreList.type ? t(`type.${scoreList.type.toLowerCase()}`) : t('type.count')}
            </p>
            <p className="text-sm italic text-secondary-600">
              {t('typeCannotChange')}
            </p>
          </>
        ) : (
          <>
            <p className="font-medium">{t('typeLabel')}</p>
            <div className="flex flex-col gap-2">
              {Object.values(ScoreCardType).map((type) => (
                <label
                  key={type}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${scoreList.type === type
                      ? 'bg-secondary-200'
                      : 'bg-secondary-50'
                    }`}
                  onClick={() => setScoreList((prev) => ({ ...prev, type }))}>
                  <input
                    type="radio"
                    name="scoreCardType"
                    checked={scoreList.type === type}
                    onChange={() => setScoreList((prev) => ({ ...prev, type }))}
                    className="h-4 w-4"
                  />
                  <span className="text-base font-medium">{t(`type.${type.toLowerCase()}`)}</span>
                </label>
              ))}
            </div>
          </>
        )}
        <p className="font-medium">{t('includeAllMembers')}</p>
        <TabButtons
          initialState={scoreList.containsAllMembers}
          containerClassName="bg-secondary-200"
          highlightClassName="bg-secondary-50"
          buttonClassName="px-2"
          buttonSelectedClassName="px-2"
          options={[
            { label: t('yes'), value: true as const },
            { label: t('no'), value: false as const },
          ]}
          onTabChange={(value) => {
            setScoreList((prev) => ({
              ...prev,
              containsAllMembers: value,
            }));
          }}
        />
        {!scoreList.containsAllMembers && (
          <ConnectionsList
            connections={membersOfCommunity}
            selected={selectedMembersIdsList}
            hasSearch={false}
            setSelected={setSelectedMembersIdsList}
            handleSelectFunction={undefined}
            handleDiselectFunction={undefined}
          />
        )}
        <Button
          variant="primary"
          className="self-end"
          onClick={() => {
            onSubmit(scoreList)
              .then(() => {
                setOpen(false);
              })
              .catch((err: Error) => {
                if (err.message === 'STOP') {
                  toast.error(errorT('emptyNameError'));
                }
              });
          }}>
          {t('createButton')}
        </Button>
      </div>
    </ModalLayout>
  );
}
